import React, { useEffect, useContext, useState, useMemo } from "react";
import { isEqual } from "lodash";
import { useTranslation } from "react-i18next";
import Layout from "@common/layout/Layout";
import { Loading, Tabs, toast } from "@foris/avocado-suite";
import { ContextEDH } from "@context/ContextEDH";
import { Context } from "../../context/GroupsManagerContext";
import { Types as DataTypes } from "../../context/data.reducer";
import { Types as EditionTypes } from "../../context/editions.reducer";
import {
  GroupsTableFiltersState,
  replaceTableFilters,
  setTableFiltersOrderBy,
  useGroupsTableFilters,
} from "../../context/useGroupsTableFilters";
import { useGetGroup } from "../../hooks/useGetGroup";
import { useGetCampuses } from "../../hooks/useGetCampuses";
import GroupDetail from "../../components/GroupDetail/GroupDetail";
import SubgroupsCreation from "../../components/SubgroupsCreation/SubgroupsCreation";
import BottomDetail from "../../components/BottomDetail/BottomDetail";
import SubgroupsTable from "../../components/SubgroupsTable/SubgroupsTable";
import useGroupsManagerCrud from "../../hooks/useGroupsManagerCrud";
import { useGetSubgroups } from "../../hooks/useGetSubgroups";
import EnrollmentsTable from "../../components/EnrollmentsTable/EnrollmentsTable";
import useGetGroupEnrollmentsByLinkId from "../../hooks/useGetGroupEnrollmentsByLinkId";
import useGetGroupEnrollmentsByGroupId from "../../hooks/useGetGroupEnrollmentsByGroupId";

import { displayingParentGroup } from "../../utils/displayingParentGroup";
import splitGroupErrors from "../../utils/splitGroupErrors";
import buildDefaultTableFilters from "../../utils/buildDefaultTableFilters";
import {
  OrderByDirection,
  GroupsManagerSearchOrderByFields,
  GroupsManagerEnrollmentsOrderByFieldsEnum,
} from "@models/ISchema";
import { AdaptedGroup, GroupEdition } from "../../models";
import { usePublicConfigStore } from "@context/usePublicConfigStore";
import useGetCapacityErrors from "../../hooks/useGetCapacityErrors";
import { getEnrollmentsRequestRoute } from "../../utils/enrollmentsRequest";
import { Header } from "@common/components";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import styles from "./groupManager.module.scss";
import dayjs from "dayjs";
import {
  EnrollmentsTableFiltersState,
  setOrderBy,
  useEnrollmentsTableFilters,
} from "../../context/useEnrollmentsTableFilters";

const ROWS_PER_PAGE = 5;
const FORCED_WAITING_TIME = 4000; // ms

type requestEnrollmentsProps = {
  page: number;
  pageSize?: number;
  orderBy?: EnrollmentsTableFiltersState["orderBy"];
  searchBy?: EnrollmentsTableFiltersState["searchBy"];
};

type requestSubgroupsProps = {
  page: number;
  pageSize?: number;
  avoidEditionsCleaning: boolean;
  orderBy: GroupsTableFiltersState["orderBy"];
  searchBy: GroupsTableFiltersState["searchBy"];
};

const GroupManagerApp: React.FC = () => {
  const darwinUrl = usePublicConfigStore(state => state.darwinLegacyUrl);
  const { t } = useTranslation();
  const { scenario, origin, workspace, id: groupId } = useContextUrl();
  const { state: outerState } = useContext(ContextEDH);
  const { state, dispatch } = useContext(Context);
  const tableFilters = useGroupsTableFilters();
  const [
    { subgroups, shortSubgroups, isLoading: subgroupsRequestLoading, subgroupsPageInfo },
    { getSubgroups, getShortGroupsData },
  ] = useGetSubgroups();
  const [{ campuses, isLoading: isLoadingCampuses }, getCampuses] = useGetCampuses();
  const [enrollments, enrollmentsRequestLoading, enrollmentsPageInfo, getEnrollments] =
    displayingParentGroup() ? useGetGroupEnrollmentsByLinkId() : useGetGroupEnrollmentsByGroupId();
  const [displayCreation, setDisplayCreation] = useState(false);
  const [avoidEditionsCleaningAfterRequest, setAvoidEditionsCleaningAfterRequest] = useState(false);
  const [group, getGroup, isLoadingGroup] = useGetGroup();
  const [{ isLoading: isLoadingGroupManagerCrud }, submitEditions] = useGroupsManagerCrud({
    scenario,
    origin,
  });
  const [tableFiltersByTab, setTableFiltersByTab] = useState<{
    campuses: GroupsTableFiltersState;
    students: GroupsTableFiltersState;
  }>({
    campuses: buildDefaultTableFilters("campuses"),
    students: buildDefaultTableFilters("enrollments"),
  });
  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingOnSave, setIsLoadingOnSave] = useState(false);
  const [internalPageSize, setInternalPageSize] = useState(ROWS_PER_PAGE);
  const [lastUpdateDateTime, setLastUpdateDateTime] = useState<string>(
    dayjs().format("DD/MM/YYYY HH:mm"),
  );
  const { orderBy: enrollmentsOrderBy, searchBy: enrollmentsSearchBy } =
    useEnrollmentsTableFilters();

  const { homeUrl } = useNavigationUrl();

  const userAbilities = outerState?.base?.base?.user?.abilities ?? {};
  const isGroupPackageType = group?.referentType === "PACKAGE";
  const isGroupParentType = group?.isReferent === true;
  const shouldHaveSubgroups = !!group?.id && isGroupParentType;
  const isLoadingSubgroups = shouldHaveSubgroups
    ? !subgroupsPageInfo || subgroupsRequestLoading
    : subgroupsRequestLoading;
  const hasEditions = useMemo(
    () => Object.keys(state?.editions?.byGroupId ?? {}).length > 0,
    [state?.editions?.byGroupId],
  );

  // This variable is used to identify if the enrollments tab has already been
  // entered. If not, the `setTableFiltersOrderBy` will be setted when the
  // enrollments tab is selected for the first time.
  let enrollmentsTabAlreadyEntered = false;

  const requestEnrollments = ({
    page,
    pageSize = internalPageSize, // useState que maneja el tamaño de la página
    orderBy,
    searchBy,
  }: requestEnrollmentsProps) => {
    if (!group) return;

    dispatch({ type: EditionTypes.Clean });
    getEnrollments(group, { page, size: pageSize }, orderBy, searchBy);

    if (pageSize && pageSize !== internalPageSize) {
      // Si el tamano por pagina que recibo cambia, actualizo el estado
      setInternalPageSize(pageSize);
    }
    setLastUpdateDateTime(dayjs().format("DD/MM/YYYY HH:mm"));
  };

  const requestEnrollmentsByGroupId = () => {
    if (enrollmentsRequestLoading) return;

    dispatch({ type: DataTypes.SetGroups, payload: [] });
    requestEnrollments({
      page: 1,
      orderBy: enrollmentsOrderBy,
      searchBy: enrollmentsSearchBy,
    });
    setLastUpdateDateTime(dayjs().format("DD/MM/YYYY HH:mm"));
  };

  const requestSubgroups = ({
    page,
    pageSize = internalPageSize,
    avoidEditionsCleaning,
    orderBy,
    searchBy,
  }: requestSubgroupsProps) => {
    if (!group || (group && !isGroupParentType)) return;
    setAvoidEditionsCleaningAfterRequest(avoidEditionsCleaning);
    getSubgroups(group, { page, size: pageSize }, orderBy, searchBy);

    if (pageSize && pageSize !== internalPageSize) {
      // Si el tamano por pagina que recibo cambia, actualizo el estado
      setInternalPageSize(pageSize);
    }
    setLastUpdateDateTime(dayjs().format("DD/MM/YYYY HH:mm"));
  };

  const onCampusesTabSelected = () => {
    setTableFiltersByTab({ ...tableFiltersByTab, students: tableFilters });
    if (tableFiltersByTab?.campuses != null) {
      replaceTableFilters(tableFiltersByTab?.campuses);
    }
  };

  const onEnrollmentsTabSelected = () => {
    setTableFiltersByTab({ ...tableFiltersByTab, campuses: tableFilters });

    // if no enrollments has been requested just yet, do it for the first time
    if (enrollments == null) {
      requestEnrollments({ page: 1 });
    }

    if (!enrollmentsTabAlreadyEntered) {
      setOrderBy({
        header: "Estudiante",
        field: GroupsManagerEnrollmentsOrderByFieldsEnum.Student,
        direction: OrderByDirection.Desc,
      });
    }

    enrollmentsTabAlreadyEntered = true;
  };

  const setDefaultOrderBy = () => {
    setTableFiltersOrderBy({
      header: "Sede",
      field: GroupsManagerSearchOrderByFields.Campus,
      direction: OrderByDirection.Desc,
    });
  };

  const onSave = async () => {
    // exclude groups with hard errors
    setIsLoadingOnSave(true);
    const [hardErrorsByGroupId] = splitGroupErrors(state?.editions?.errorsByGroupId);
    const hardErrorGroupIds = new Set(Object.keys(hardErrorsByGroupId));
    const editionsToSubmit = Object.fromEntries(
      Object.entries(state?.editions?.byGroupId ?? {}).filter(
        ([groupId]) => !hardErrorGroupIds.has(groupId),
      ),
    ) as Record<AdaptedGroup["id"], GroupEdition>;

    await submitEditions(editionsToSubmit);

    const onSaveTimeout = setTimeout(() => {
      for (const [groupId, edition] of Object.entries(editionsToSubmit)) {
        if (edition?.capacity !== undefined) {
          dispatch({
            type: EditionTypes.AddPendingCapacity,
            payload: { groupId, capacity: edition?.capacity },
          });
        }
      }

      dispatch({ type: EditionTypes.Clean });
      requestSubgroups({
        page: subgroupsPageInfo?.page ?? 1,
        avoidEditionsCleaning: false,
        orderBy: tableFilters?.orderBy,
        searchBy: tableFilters?.searchBy,
      });

      toast.warning(t("groups-manager.notifications.change-request-sent"), {
        autoClose: 3000,
      });

      setIsLoadingOnSave(false);
      clearTimeout(onSaveTimeout);
    }, FORCED_WAITING_TIME);
  };

  const totalCapacity = useMemo(() => {
    if (isLoadingGroup || !group?.id || isLoadingSubgroups) return 0;

    if (!(shortSubgroups ?? [])?.length || !shouldHaveSubgroups) {
      return group?.capacity ?? 0;
    }

    const adaptedCapacities = [...(shortSubgroups ?? [])].map(subgroup => {
      const byGroupId = state?.editions?.byGroupId ?? {};
      const pendingEditionsByGroupId = state?.editions?.pendingCapacitiesByGroupId ?? {};

      return (
        pendingEditionsByGroupId[subgroup?.id] ??
        byGroupId[subgroup?.id]?.capacity ??
        subgroup?.capacity
      );
    });

    return adaptedCapacities.reduce((acc, val) => acc + val, 0);
  }, [
    group,
    shortSubgroups,
    state?.editions?.byGroupId,
    state?.editions?.pendingCapacitiesByGroupId,
    isLoadingGroup,
    isLoadingSubgroups,
    shouldHaveSubgroups,
  ]);

  useEffect(() => {
    dispatch({ type: DataTypes.Clean, payload: undefined });
    dispatch({ type: EditionTypes.Clean });
    getGroup(scenario, origin, groupId);
    setDefaultOrderBy();

    return () => {
      dispatch({ type: DataTypes.Clean, payload: undefined });
      dispatch({ type: EditionTypes.Clean });
    };
  }, []);

  /**
   * Update the context's `groups` after request
   */
  useEffect(() => {
    if (subgroups === null) return;
    const requestWasActuallyMade =
      !isEqual(subgroups, state?.data?.groups) &&
      !isEqual(subgroupsPageInfo, state?.data?.groupsPageInfo);

    if (!avoidEditionsCleaningAfterRequest && requestWasActuallyMade) {
      dispatch({ type: EditionTypes.Clean });
      setAvoidEditionsCleaningAfterRequest(false);
    }

    dispatch({ type: DataTypes.SetGroups, payload: subgroups ?? [] });
    dispatch({ type: DataTypes.SetGroupsById, payload: subgroups ?? [] });
    dispatch({ type: DataTypes.SetGroupsPageInfo, payload: subgroupsPageInfo });
    dispatch({ type: EditionTypes.SanitizePendingEditions, payload: subgroups ?? [] });
  }, [subgroups, subgroupsPageInfo]);

  /**
   * After the enrollments are requested, set loading as false and clean
   * any editions made.
   */
  useEffect(() => {
    if (enrollments === null) return;
    dispatch({ type: EditionTypes.Clean });
  }, [enrollments]);

  useEffect(() => {
    if (!group) return;

    if (isGroupParentType) {
      getCampuses(scenario, origin);
    }

    requestSubgroups({
      page: subgroupsPageInfo?.page ?? 1,
      avoidEditionsCleaning: false,
      orderBy: tableFilters?.orderBy,
      searchBy: tableFilters?.searchBy,
    });

    if (shouldHaveSubgroups) {
      getShortGroupsData(
        group,
        { page: 1, size: 100 },
        tableFilters?.orderBy,
        tableFilters?.searchBy,
      );
    }

    dispatch({ type: DataTypes.SetGroup, payload: group });

    if (!displayingParentGroup()) {
      requestEnrollmentsByGroupId();
    } else if (enrollments === null) {
      // Load the parent group's student count before its enrollments tab opens.
      requestEnrollments({ page: 1 });
    }
  }, [group]);

  useEffect(() => {
    const requestRoute = getEnrollmentsRequestRoute(
      activeTab,
      group,
      enrollments,
      displayingParentGroup(),
    );
    if (requestRoute === "parent") {
      requestSubgroups({
        page: 1,
        avoidEditionsCleaning: false,
        orderBy: tableFilters?.orderBy,
        searchBy: tableFilters?.searchBy,
      });
      // request enrollments by the group's CRN
      requestEnrollments({
        page: 1,
        orderBy: enrollmentsOrderBy,
        searchBy: enrollmentsSearchBy,
      });
    } else if (requestRoute === "subgroup") {
      requestEnrollmentsByGroupId();
    }
  }, [activeTab]);

  /**
   * Build and return the groups report's url.
   */
  const getReportUrl = () => {
    const params = `scenario_id=${scenario}&group_id=${state?.data?.group?.id}`;
    return `${darwinUrl}/groups_manager/enrollments/${origin}?${params}`;
  };

  const isLoadingGeneralData = isLoadingGroupManagerCrud || isLoadingOnSave;

  const errorsObj = useGetCapacityErrors({
    group: state?.data?.group,
    subGroups: state?.data?.groups,
    editionsByGroupId: state?.editions?.byGroupId,
  });

  const hasCapacityError =
    totalCapacity > state?.data?.group?.groupCapacity?.calculatedCapacity && hasEditions;

  const getSubgroupsView = () => {
    return displayCreation ? (
      <SubgroupsCreation
        campuses={campuses ?? []}
        parentGroup={state?.data?.group}
        requestSubgroups={() => {
          shouldHaveSubgroups &&
            getShortGroupsData(
              group,
              { page: 1, size: 100 },
              tableFilters?.orderBy,
              tableFilters?.searchBy,
            );
          requestSubgroups({
            page: 1,
            avoidEditionsCleaning: false,
            orderBy: tableFilters?.orderBy,
            searchBy: tableFilters?.searchBy,
          });
        }}
        setDisplayCreation={setDisplayCreation}
        subgroups={shortSubgroups as AdaptedGroup[]}
      />
    ) : (
      <>
        <SubgroupsTable
          isGroupPackageType={isGroupPackageType}
          subgroups={subgroups}
          pageInfo={subgroupsPageInfo}
          requestSubgroups={requestSubgroups}
          capacitiesSumUp={totalCapacity}
          isLoading={isLoadingGroup || isLoadingSubgroups}
          isLoadingCampuses={isLoadingCampuses}
          pageSize={internalPageSize}
          setDisplayCreation={setDisplayCreation}
          errorReason={errorsObj.errorKey as any}
          hasHardError={errorsObj.hasHardErrors}
          lastUpdateDateTime={lastUpdateDateTime}
        />
        {Boolean(userAbilities?.can_edit_groups_in_group_manager) && (
          <BottomDetail
            onSave={onSave}
            isLoading={
              isLoadingGroupManagerCrud || isLoadingOnSave || isLoadingGroup || isLoadingSubgroups
            }
            externalErrors={errorsObj.errorsByGroupId}
            parentGroupHasError={hasCapacityError}
          />
        )}
      </>
    );
  };

  return (
    <Layout contextSearch>
      {isLoadingGeneralData ? <Loading /> : null}

      <div className={styles.groupManagerApp}>
        <Header
          breadcrumbItems={[
            {
              path: homeUrl(workspace, scenario, origin),
              title: t("sidebar-menu.titles.home"),
            },
            {
              title: t("groups-manager.title"),
            },
          ]}
          title={t("groups-manager.title")}
        />

        <div className={styles.groupManagerApp_content}>
          <GroupDetail
            isLoading={
              isLoadingGeneralData ||
              !enrollmentsPageInfo ||
              isLoadingGroup ||
              isLoadingSubgroups ||
              !group?.id ||
              !state?.data?.group?.id
            }
            capacitiesSumUp={totalCapacity}
            displayCapacitiesError={hasCapacityError}
            group={group}
            groups={(shortSubgroups ?? []) as AdaptedGroup[]}
          />

          {displayingParentGroup() ? (
            <Tabs
              className={styles.contentTabs}
              onClick={(idx: number) => {
                setActiveTab(idx);
                if (idx === 0) {
                  onCampusesTabSelected();
                } else if (idx === 1) {
                  onEnrollmentsTabSelected();
                } else {
                  throw new Error("Tab index not supported yet");
                }
              }}
              items={[
                {
                  label: t("groups-manager.group-details.tabs.groups", {
                    count: state?.data?.groupsPageInfo?.total ?? 0,
                  }),
                  children: getSubgroupsView(),
                },
                {
                  label: t("groups-manager.group-details.tabs.students", {
                    count: enrollmentsPageInfo?.total ?? 0,
                  }),
                  disabled: displayCreation,
                  children: (
                    <EnrollmentsTable
                      showStudentsGroup
                      reportUrl={getReportUrl()}
                      enrollments={enrollments ?? []}
                      enrollmentsPageInfo={enrollmentsPageInfo}
                      isLoading={enrollmentsRequestLoading || isLoadingGroup || isLoadingSubgroups}
                      pageSize={internalPageSize}
                      lastUpdateDateTime={lastUpdateDateTime}
                      requestEnrollments={(page: number, pageSize?: number) =>
                        requestEnrollments({
                          page,
                          pageSize: pageSize ?? internalPageSize,
                          orderBy: enrollmentsOrderBy,
                          searchBy: enrollmentsSearchBy,
                        })
                      }
                    />
                  ),
                },
              ]}
            />
          ) : null}

          {!displayingParentGroup() && (
            <EnrollmentsTable
              reportUrl={getReportUrl()}
              enrollments={enrollments ?? []}
              enrollmentsPageInfo={enrollmentsPageInfo}
              isLoading={enrollmentsRequestLoading || isLoadingGroup || isLoadingSubgroups}
              pageSize={internalPageSize}
              lastUpdateDateTime={lastUpdateDateTime}
              requestEnrollments={(page: number, pageSize?: number) =>
                requestEnrollments({
                  page,
                  pageSize: pageSize ?? internalPageSize,
                  orderBy: enrollmentsOrderBy,
                  searchBy: enrollmentsSearchBy,
                })
              }
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GroupManagerApp;
